import { useMemo } from 'react';
import _get from 'lodash/get';
import dayjs, { Dayjs } from 'dayjs';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { transformDate } from '@lib/utils/dateTimeHelpers';

type Args = {
  filterParams?: { period?: string[] } | null;
};

type Result = {
  defaultMinDateObj?: Dayjs;
  defaultMaxDateObj?: Dayjs;
  hasStoredPeriodFilter: boolean;
  periodValue: string[];
};

export default function useTaskReportingPeriod({ filterParams }: Args): Result {
  // Reads ProtectedRoute's single whoami query result — see useWhoamiContext.
  const { currentUser } = useWhoamiContext();

  const defaultMinDate = _get(currentUser, 'company.createdAt') as
    string | undefined;
  const defaultMaxDate = useMemo(() => new Date().toISOString(), []);

  const defaultMinDateObj = defaultMinDate
    ? dayjs(defaultMinDate).startOf('day')
    : undefined;
  const defaultMaxDateObj = defaultMaxDate
    ? dayjs(defaultMaxDate).endOf('day')
    : undefined;

  const defaultMinDateStr = defaultMinDateObj
    ? transformDate(defaultMinDateObj.toDate())
    : undefined;
  const defaultMaxDateStr = defaultMaxDateObj
    ? transformDate(defaultMaxDateObj.toDate(), true)
    : undefined;

  const filterParamsPeriod = filterParams?.period;
  const basePeriodStartParam =
    (Array.isArray(filterParamsPeriod) && filterParamsPeriod[0]) ||
    defaultMinDateStr;
  const basePeriodEndParam =
    (Array.isArray(filterParamsPeriod) && filterParamsPeriod[1]) ||
    defaultMaxDateStr;

  const periodValue = useMemo(() => {
    if (basePeriodStartParam && basePeriodEndParam)
      return [basePeriodStartParam, basePeriodEndParam];
    return [] as string[];
  }, [basePeriodEndParam, basePeriodStartParam]);

  const hasStoredPeriodFilter = !!(
    Array.isArray(filterParamsPeriod) &&
    filterParamsPeriod.length === 2 &&
    filterParamsPeriod[0] &&
    filterParamsPeriod[1]
  );

  return {
    defaultMinDateObj,
    defaultMaxDateObj,
    hasStoredPeriodFilter,
    periodValue,
  };
}
